/** @format */

import {compareAsc, format, parse} from 'date-fns';

import {ru} from 'date-fns/locale';

import {
  AreaChartData,
  AreaChartMultiDataType,
  AreaChartRecord,
  TooltipDataList,
  TooltipRow,
} from 'components/charts/AreaChartMultiData';

import {sensorName} from 'helpers/sensor_names';

import {GraphSensorCheckType} from '../../types';

type OneDayRecord = {
  date: Date;
  dateDisplay: string; // 1 июня 2025
  recordKey: string; // 2025-06-01
  month?: string; // июнь
  sensors: AreaChartRecord;
};

const defaultTooltipRow: TooltipRow = {
  label: '0',
  value: '',
};

const getGraphDataByCriteria = (
  sensors: string[],
  graphSensorChecks: ReadonlyArray<GraphSensorCheckType>,
): AreaChartData => {
  const collection: Record<string, OneDayRecord> = {};

  // Только сенсоры, указанные в проекте

  const filteredChecks = graphSensorChecks.filter(check => sensors.includes(check.sensorName));

  // Создать коллекцию всех необходимых значений по каждому дню

  let needMonth = true;

  filteredChecks.forEach(check => {
    const recordKey = check.collectedOn;
    const date = parse(recordKey, 'yyyy-MM-dd', new Date());
    const dateDisplay = format(date, 'd MMMM yyyy', {locale: ru});
    const sensorId = check.sensorName;
    const amount = check.new + check.old;

    let month;
    const day = date.getDate();
    if (needMonth || day === 1) {
      month = format(date, 'LLLL', {locale: ru});
      needMonth = false;
    }

    const dayRecord: OneDayRecord = {
      date,
      recordKey,
      dateDisplay,
      month,
      sensors: {
        [sensorId]: amount,
      },
    };

    if (!collection[recordKey]) {
      collection[recordKey] = dayRecord;
    } else {
      const sensorAmount = collection[recordKey].sensors[sensorId];

      collection[recordKey] = {
        ...collection[recordKey],
        sensors: {
          ...collection[recordKey].sensors,
          [sensorId]: sensorAmount === undefined ? amount : sensorAmount + amount,
        },
      };
    }
  });

  // Массив со всеми датами из коллекции

  const allRecordKeys = Object.keys(collection);

  // Сортировка дат по возрастанию

  allRecordKeys.sort((dateA, dateB) => {
    return compareAsc(collection[dateA].date, collection[dateB].date);
  });

  // Формирование результирующего массива для графиков

  const graphData: AreaChartMultiDataType[] = allRecordKeys.map(recordKey => {
    const sensors = collection[recordKey].sensors;
    const resultData: AreaChartMultiDataType = {
      name: recordKey,
      ...sensors,
    };
    if (collection[recordKey].month) {
      resultData.month = collection[recordKey].month;
    }
    return resultData;
  });

  // Формирование данных для тултипа

  const tooltipData: TooltipDataList = {};

  allRecordKeys.forEach(recordKey => {
    const sensors = collection[recordKey].sensors;
    const sensorIds = Object.keys(collection[recordKey].sensors);

    // Берем только не нулевые значения
    const tooltipRows = sensorIds
      .filter(sensorId => sensors[sensorId])
      .map(sensorId => ({
        label: sensorName(sensorId),
        value: String(sensors[sensorId]),
      }));

    tooltipData[recordKey] = {
      dateDisplay: collection[recordKey].dateDisplay,
      tooltipRows: tooltipRows.length ? tooltipRows : [defaultTooltipRow],
    };
  });

  return {
    graphData,
    tooltipData,
  };
};

export {getGraphDataByCriteria};
